import { API_MESSAGES } from '../constants/ApiConstants';
import {
  clearStoredAuthTokens,
  getStoredAuthTokens,
  isJwtExpired,
  setStoredAuthTokens,
  StoredAuthTokens,
} from './authTokenStorage';
import { buildApiUrl, requestJson } from './apiClient';
import { getAcquisitionAttribution } from './acquisitionAttribution';

type AuthRequestPayload = Record<string, unknown>;

export type LoginResponse = {
  message: string;
  accessToken?: string;
  idToken?: string;
  expiresIn?: number;
  tokenType?: string;
  challengeName?: string;
};

export type CheckEmailResponse = {
  message: string;
  exists: boolean;
};

export type SignUpResponse = {
  message: string;
  userSub: string;
  userConfirmed: boolean;
};

export type ConfirmSignUpResponse = {
  message: string;
  session?: string;
};

export type ForgotPasswordResponse = {
  message: string;
  delivery?: {
    attributeName?: string;
    deliveryMedium?: string;
    destination?: string;
  };
};

export type ConfirmForgotPasswordResponse = {
  message: string;
};

export type ChangePasswordResponse = {
  message: string;
};

export type SessionPreferenceResponse = {
  message: string;
};

export class AuthApiError extends Error {
  public readonly statusCode: number;
  public readonly errorCode?: string;

  constructor(message: string, statusCode: number, errorCode?: string) {
    super(message);
    this.name = 'AuthApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
  }
}

async function authRequest<TResponse>(
  path: string,
  payload?: AuthRequestPayload,
  method: 'GET' | 'POST' = 'POST',
  credentials: RequestCredentials = 'same-origin',
): Promise<TResponse> {
  const { response, body: responseBody } = await requestJson<TResponse>(buildApiUrl(path), {
    method,
    credentials,
    payload,
  });

  if (!response.ok) {
    throw new AuthApiError(
      responseBody?.message ?? API_MESSAGES.AUTH_REQUEST_FAILED,
      response.status,
      responseBody?.error,
    );
  }

  return (responseBody ?? {}) as TResponse;
}

function requireStoredAuthTokens(): StoredAuthTokens {
  const tokens = getStoredAuthTokens();
  if (!tokens?.accessToken || !tokens?.idToken) {
    throw new AuthApiError(API_MESSAGES.REAUTH_REQUIRED, 401, 'MissingAuthTokens');
  }

  return tokens;
}

function storeSessionTokens(response: LoginResponse, currentTokens: StoredAuthTokens | null = getStoredAuthTokens()) {
  if (!response.accessToken || !response.idToken) {
    throw new AuthApiError(API_MESSAGES.REAUTH_REQUIRED, 401, 'MissingSessionTokens');
  }

  const updatedTokens: StoredAuthTokens = {
    ...currentTokens,
    accessToken: response.accessToken,
    idToken: response.idToken,
    expiresIn: response.expiresIn,
    tokenType: response.tokenType,
  };
  setStoredAuthTokens(updatedTokens);
  return updatedTokens;
}

async function restoreSessionTokens(): Promise<StoredAuthTokens> {
  const refreshed = await refreshSession();
  return storeSessionTokens(refreshed);
}

async function authenticatedAuthRequest<TResponse>(
  path: string,
  payload: AuthRequestPayload,
  tokens: StoredAuthTokens = requireStoredAuthTokens(),
  allowRefresh = true,
): Promise<TResponse> {
  if (!tokens.accessToken) {
    if (allowRefresh) {
      return authenticatedAuthRequest<TResponse>(path, payload, await restoreSessionTokens(), false);
    }
    throw new AuthApiError(API_MESSAGES.REAUTH_REQUIRED, 401, 'MissingAccessToken');
  }

  const { response, body: responseBody } = await requestJson<TResponse>(buildApiUrl(path), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${tokens.accessToken}`,
    },
    payload,
  });

  if (!response.ok) {
    const isTokenExpiry403 = response.status === 403 && isJwtExpired(tokens.idToken ?? '');
    if ((response.status === 401 || isTokenExpiry403) && allowRefresh) {
      try {
        return authenticatedAuthRequest<TResponse>(path, payload, await restoreSessionTokens(), false);
      } catch (error) {
        clearStoredAuthTokens();
        throw error;
      }
    }

    throw new AuthApiError(
      responseBody?.message ?? API_MESSAGES.AUTH_REQUEST_FAILED,
      response.status,
      responseBody?.error,
    );
  }

  return (responseBody ?? {}) as TResponse;
}

export function signUp(email: string, password: string) {
  return authRequest<SignUpResponse>('/auth/signup', {
    email,
    password,
    attribution: getAcquisitionAttribution(),
  });
}

export function checkEmail(email: string) {
  return authRequest<CheckEmailResponse>('/auth/check-email', { email });
}

export function confirmSignUp(email: string, confirmationCode: string) {
  return authRequest<ConfirmSignUpResponse>('/auth/confirm-signup', {
    email,
    confirmationCode,
    attribution: getAcquisitionAttribution(),
  });
}

export function login(email: string, password: string) {
  return authRequest<LoginResponse>('/auth/login', { email, password, rememberMe: false }, 'POST', 'include');
}

export function loginWithRememberMe(email: string, password: string, rememberMe: boolean) {
  return authRequest<LoginResponse>('/auth/login', { email, password, rememberMe }, 'POST', 'include');
}

export async function refreshSession() {
  const response = await authRequest<LoginResponse>(
    '/auth/refresh',
    undefined,
    'POST',
    'include',
  );
  storeSessionTokens(response);
  return response;
}

export async function logout() {
  try {
    return await authRequest<{ message: string }>('/auth/logout', undefined, 'POST', 'include');
  } finally {
    clearStoredAuthTokens();
  }
}

export function updateSessionPreference(rememberMe: boolean) {
  return authRequest<SessionPreferenceResponse>(
    '/auth/session-preferences',
    { rememberMe },
    'POST',
    'include',
  );
}

export function forgotPassword(email: string) {
  return authRequest<ForgotPasswordResponse>('/auth/forgot-password', { email });
}

export function confirmForgotPassword(email: string, confirmationCode: string, newPassword: string) {
  return authRequest<ConfirmForgotPasswordResponse>('/auth/confirm-forgot-password', {
    email,
    confirmationCode,
    newPassword,
  });
}

export function resendConfirmation(email: string) {
  return authRequest<ForgotPasswordResponse>('/auth/resend-confirmation', { email });
}

export function changePassword(currentPassword: string, newPassword: string) {
  return authenticatedAuthRequest<ChangePasswordResponse>('/auth/change-password', {
    currentPassword,
    newPassword,
  });
}
